// 주간 시간표 공용 주 전환 — 관리자/선생님 레슨 시간표(v1, v2)에서 쓴다.
// 주가 바뀌면 load(weekStart)로 그 주 데이터를 받아 한 번에 반영하고, 반영하는 순간 모션을 재생한다.
// (날짜 → 예약 → 휴일이 따로 바뀌면 주 전환이 잘 느껴지지 않는다)
// 모션은 화면에 반영돼 있던 주와의 실제 거리로 고른다 — 1주: 슬라이드, 2주 이상: 페이드+확대.
// 응답이 LOADING_DELAY보다 오래 걸릴 때만 loading=true (이동 바 아래 진행 막대용, 짧은 깜빡임 방지).
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { motionForDistance, nextMotion } from "../utils/calendarMotion";

const LOADING_DELAY = 150;

// load: (weekStart) => Promise<() => void> — 데이터를 받은 뒤 state에 반영하는 함수를 돌려준다.
export default function useWeekTransition(weekStart, load) {
  const loadRef = useRef(load);
  loadRef.current = load;
  const requestedWeekRef = useRef(weekStart); // 가장 최근에 요청한 주 — 늦게 온 이전 응답을 버리는 기준
  const shownWeekRef = useRef(weekStart);     // 화면에 반영된 주 — 모션 거리 기준
  const motionTimer = useRef(null);
  const [motion, setMotion] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => () => clearTimeout(motionTimer.current), []);

  useEffect(() => {
    requestedWeekRef.current = weekStart;
    const loadingTimer = setTimeout(() => setLoading(true), LOADING_DELAY);
    const isCurrent = () => requestedWeekRef.current === weekStart;

    loadRef.current(weekStart)
      .then((apply) => {
        if (!isCurrent()) return; // 그사이 다른 주로 이동했으면 버린다
        clearTimeout(loadingTimer);
        setLoading(false);
        apply();

        const weeks = dayjs(weekStart).diff(dayjs(shownWeekRef.current), "day") / 7;
        shownWeekRef.current = weekStart;
        const type = motionForDistance(Math.round(weeks));
        if (type) {
          setMotion((prev) => nextMotion(prev, type));
          clearTimeout(motionTimer.current);
          motionTimer.current = setTimeout(() => setMotion(null), 300);
        }
      })
      .catch(() => {
        if (!isCurrent()) return;
        clearTimeout(loadingTimer);
        setLoading(false);
      });

    return () => clearTimeout(loadingTimer);
  }, [weekStart]);

  return { motion, loading };
}
